import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getDeclinePreviewApi, declineInviteByTokenApi } from "@/portals/patient/api/familyApi";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { Spinner } from "@/shared/components/spinner";
import { toastError } from "@/shared/lib/toast";
import type { InvitePreview } from "@/shared/types/family";

type PageState = "loading" | "invalid" | "confirm" | "declined";

/**
 * Reached from the "Not interested? Decline this invitation" link in either
 * caregiver invite email — deliberately unauthenticated, so someone can say
 * no without ever signing up or logging in. Requires an explicit click here
 * (never declines on page load) so an email client's link-prescanning can
 * never trigger this by itself — see FamilyService.decline_invite_by_token.
 */
export function DeclineInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [state, setState] = useState<PageState>("loading");
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    let cancelled = false;
    getDeclinePreviewApi(token).then(({ data }) => {
      if (cancelled) return;
      if (!data) {
        setState("invalid");
        return;
      }
      setPreview(data);
      setState("confirm");
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleDecline = async () => {
    if (!token) return;
    setDeclining(true);
    const { error } = await declineInviteByTokenApi(token);
    setDeclining(false);
    if (error) {
      toastError(error);
      return;
    }
    setState("declined");
  };

  if (state === "loading") {
    return (
      <div className="flex w-full max-w-sm items-center justify-center py-16">
        <Spinner size="large" />
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]">
          <Icon icon="lucide:link-2-off" className="h-6 w-6 text-[#9CA3AF]" />
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">This link isn't valid</h2>
        <p className="!mt-2 text-sm text-[#9CA3AF]">
          This invitation may have already been accepted, declined, or the link is incorrect.
        </p>
        <Link to="/patient/auth/signin" className="mt-6 inline-block text-sm font-semibold text-[#E4002B] hover:underline">
          Go to sign in
        </Link>
      </div>
    );
  }

  if (state === "declined") {
    return (
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#DCFCE7]">
          <Icon icon="lucide:check" className="h-6 w-6 text-[#16A34A]" />
        </div>
        <h2 className="text-xl font-extrabold text-[#0A2947]">Invitation declined</h2>
        <p className="!mt-2 text-sm text-[#9CA3AF]">
          {preview?.patientName ? `${preview.patientName} has` : "They've"} been notified that you won't be accepting this invitation.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF0F3]">
        <Icon icon="lucide:heart-handshake" className="h-6 w-6 text-[#E4002B]" />
      </div>
      <h2 className="text-center text-xl font-extrabold text-[#0A2947]">Decline this invitation?</h2>
      <p className="!mt-2 text-center text-sm text-[#9CA3AF]">
        <span className="font-semibold text-[#374151]">{preview?.patientName}</span> invited you to view their health records
        {preview && preview.categories.length > 0 && (
          <> — <span className="font-medium text-[#374151]">{preview.categories.join(", ")}</span></>
        )}
        . You can decline without creating an account.
      </p>

      <div className="mt-8 flex flex-col gap-3">
        <AuthSubmitButton htmlType="button" onClick={handleDecline} loading={declining}>
          Decline Invitation
        </AuthSubmitButton>
        <div className="flex justify-center">
          <PageBackButton onClick={() => window.history.back()} ariaLabel="Never mind, go back" />
        </div>
      </div>
    </div>
  );
}
